import deepEqual from "fast-deep-equal";
import type { ApiResponse } from "@entrilia/shared/api/types";
import type { StateAction } from "@entrilia/shared/types";
import { distinct, sumBy } from "@entrilia/shared/utilities/arrayHelper";
import type {
  PreviewInvoiceCardAutoFill,
  PreviewInvoiceCardCheck,
  PreviewInvoiceCardResponse,
  PreviewInvoiceCardRequest,
} from "../../../../api/types/expenseManagementTypes";
import type { FieldIdToValueMap, RelatedObject } from "../../../../api/types/objectTypes";
import type {
  NotesByCell,
  RelatedColumnLayout,
  RelatedObjectCellDecorations,
} from "../../../entityFields/RelatedObjectsGrid";
import type { RelatedObjectValuesState } from "../../../entityFields/relatedObjectValuesState";
import { updateRowAction } from "../../../entityFields/relatedObjectValuesState";

export interface InvoiceCardPreviewConfig {
  getPreview: (
    objectId: string,
    request: PreviewInvoiceCardRequest
  ) => Promise<ApiResponse<PreviewInvoiceCardResponse>>;
  fieldIds: string[];
  defaultsTriggerFieldIds: string[];
  relatedObjectType: string;
  relatedFieldIds: string[];
  relatedDefaultsTriggerFieldIds: string[];
  autoFillTooltipsBySource: Record<string, string>;
  onAutoFillOverridden?: (objectId: string, fill: PreviewInvoiceCardAutoFill, change: AutoFillOverride) => void;
  relatedColumnLayouts?: Record<string, RelatedColumnLayout>;
  getRelatedNoOptionsTexts?: (fieldValues: FieldIdToValueMap) => Record<string, string>;
  summaryDifference?: SummaryDifference;
}

export interface SummaryDifference {
  label: string;
  amountFieldId: string;
  lineAmountFieldId: string;
}

export interface AutoFillOverride {
  from: string;
  to: string;
}

export interface InvoiceCardPreviewSession {
  fieldValues: FieldIdToValueMap;
  rowValues: Record<string, FieldIdToValueMap>;
  autoFills?: PreviewInvoiceCardAutoFill[];
  answeredNewRowIds?: string[];
  defaultsMissed?: boolean;
}

export const initialInvoiceCardPreviewSession: InvoiceCardPreviewSession = { fieldValues: {}, rowValues: {} };

const normalize = (value: unknown) => (value === "" || value === null ? undefined : value);

const isSameValue = (a: unknown, b: unknown) => deepEqual(normalize(a), normalize(b));

const pick = (values: FieldIdToValueMap, fieldIds: string[]): FieldIdToValueMap =>
  Object.fromEntries(fieldIds.filter((id) => id in values).map((id) => [id, values[id]]));

const fallbackAutoFillTooltip = "Filled in automatically.";

const getAutoFillSources = (fills: PreviewInvoiceCardAutoFill[], values: FieldIdToValueMap): Record<string, string> =>
  Object.fromEntries(
    fills
      .filter((fill) => isSameValue(values[fill.fieldId], fill.value))
      .flatMap((fill) => (fill.source === undefined ? [] : [[fill.fieldId, fill.source]]))
  );

export const getInvoiceCardPreviewRequest = (
  config: InvoiceCardPreviewConfig,
  session: InvoiceCardPreviewSession,
  storedFieldValues: FieldIdToValueMap,
  fieldValues: FieldIdToValueMap,
  storedRows: RelatedObject[],
  state: RelatedObjectValuesState
): PreviewInvoiceCardRequest => {
  const rows = state.rowsByObjectType[config.relatedObjectType] ?? [];
  const defaultsForFieldIds = config.defaultsTriggerFieldIds.filter((id) => {
    const appliedFor = id in session.fieldValues ? session.fieldValues[id] : storedFieldValues[id];
    return !isSameValue(fieldValues[id], appliedFor);
  });
  const autoFillsToResend = defaultsForFieldIds.length > 0 ? session.autoFills : undefined;

  return {
    fieldValuesUserInput: pick(fieldValues, config.fieldIds),
    defaultsForFieldIds,
    relatedObjects: rows
      .filter((row) => row.rowState.updateStatus !== undefined)
      .map((row) => {
        const appliedFor = session.rowValues[row.id] ?? storedRows.find((r) => r.id === row.id)?.fieldValues ?? {};
        const defaultsRequested = config.relatedDefaultsTriggerFieldIds.some(
          (id) => !isSameValue(row[id], appliedFor[id])
        );
        return {
          key: row.id,
          objectId: row.rowState.updateStatus === "new" ? undefined : row.id,
          fieldValuesUserInput: pick(row, config.relatedFieldIds),
          defaultsRequested,
          newRowDefaultsRequested:
            defaultsForFieldIds.length === 0 &&
            row.rowState.updateStatus === "new" &&
            !session.answeredNewRowIds?.includes(row.id),
          defaultsAppliedFor: defaultsRequested ? pick(appliedFor, config.relatedDefaultsTriggerFieldIds) : undefined,
          ...(autoFillsToResend && {
            autoFillSources: getAutoFillSources(
              autoFillsToResend.filter((fill) => fill.relatedObjectKey === row.id),
              row
            ),
          }),
        };
      }),
    deletedRelatedObjectIds: [...(state.objectIdsToDeleteByObjectType[config.relatedObjectType] ?? [])],
    ...(autoFillsToResend && {
      autoFillSources: getAutoFillSources(
        autoFillsToResend.filter((fill) => fill.relatedObjectKey === undefined),
        fieldValues
      ),
    }),
  };
};

export const requestsDefaults = (request: PreviewInvoiceCardRequest): boolean =>
  request.defaultsForFieldIds.length > 0 ||
  request.relatedObjects.some((x) => x.defaultsRequested || x.newRowDefaultsRequested);

export const acknowledgeDefaultsAction =
  (
    config: InvoiceCardPreviewConfig,
    request: PreviewInvoiceCardRequest,
    preview: PreviewInvoiceCardResponse
  ): StateAction<InvoiceCardPreviewSession> =>
  (session) => {
    if (!requestsDefaults(request)) {
      return session;
    }

    const filledFieldValues = Object.fromEntries(
      preview.defaults.filter((d) => d.relatedObjectKey === undefined).map((d) => [d.fieldId, d.value])
    );

    return {
      ...session,
      fieldValues: {
        ...session.fieldValues,
        ...pick(request.fieldValuesUserInput, request.defaultsForFieldIds),
        ...pick(filledFieldValues, config.defaultsTriggerFieldIds),
      },
      rowValues: {
        ...session.rowValues,
        ...Object.fromEntries(
          request.relatedObjects
            .filter((x) => x.defaultsRequested)
            .map((x) => [x.key, pick(x.fieldValuesUserInput, config.relatedDefaultsTriggerFieldIds)])
        ),
      },
      answeredNewRowIds: [
        ...(session.answeredNewRowIds ?? []),
        ...request.relatedObjects.filter((x) => x.newRowDefaultsRequested).map((x) => x.key),
      ],
    };
  };

export const rememberAutoFillsAction =
  (preview: PreviewInvoiceCardResponse): StateAction<InvoiceCardPreviewSession> =>
  (session) => {
    const isFilledAgain = (earlier: PreviewInvoiceCardAutoFill) =>
      [...preview.autoFilled, ...preview.defaults].some(
        (fill) => fill.relatedObjectKey === earlier.relatedObjectKey && fill.fieldId === earlier.fieldId
      );
    const autoFills = [...(session.autoFills ?? []).filter((fill) => !isFilledAgain(fill)), ...preview.autoFilled];
    return deepEqual(session.autoFills, autoFills) ? session : { ...session, autoFills };
  };

export const giveUpDefaultsAction =
  (config: InvoiceCardPreviewConfig, request: PreviewInvoiceCardRequest): StateAction<InvoiceCardPreviewSession> =>
  (session) => {
    if (!requestsDefaults(request)) {
      return session;
    }

    const noPreview: PreviewInvoiceCardResponse = { fieldValues: {}, defaults: [], autoFilled: [], checks: [] };

    return { ...acknowledgeDefaultsAction(config, request, noPreview)(session), defaultsMissed: true };
  };

export const getFieldDefaults = (
  request: PreviewInvoiceCardRequest,
  preview: PreviewInvoiceCardResponse,
  fieldValues: FieldIdToValueMap
): FieldIdToValueMap =>
  Object.fromEntries(
    preview.defaults
      .filter(
        (d) =>
          d.relatedObjectKey === undefined &&
          isSameValue(fieldValues[d.fieldId], request.fieldValuesUserInput[d.fieldId])
      )
      .filter((d) => !isSameValue(fieldValues[d.fieldId], d.value))
      .map((d) => [d.fieldId, d.value ?? ""])
  );

export const applyRowDefaultsAction =
  (
    config: InvoiceCardPreviewConfig,
    request: PreviewInvoiceCardRequest,
    preview: PreviewInvoiceCardResponse,
    storedRows: RelatedObject[]
  ): StateAction<RelatedObjectValuesState> =>
  (state) =>
    preview.defaults.reduce((current, fill) => {
      const row = current.rowsByObjectType[config.relatedObjectType]?.find((r) => r.id === fill.relatedObjectKey);
      if (!row) {
        return current;
      }

      const sentValues =
        request.relatedObjects.find((x) => x.key === row.id)?.fieldValuesUserInput ??
        storedRows.find((r) => r.id === row.id)?.fieldValues ??
        {};
      if (!isSameValue(row[fill.fieldId], sentValues[fill.fieldId]) || isSameValue(row[fill.fieldId], fill.value)) {
        return current;
      }

      return updateRowAction(config.relatedObjectType, { ...row, [fill.fieldId]: fill.value ?? "" })(current);
    }, state);

const getTooltipsOfUnchangedFills = (
  autoFilled: PreviewInvoiceCardAutoFill[],
  values: FieldIdToValueMap,
  tooltipsBySource: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    autoFilled
      .filter((fill) => isSameValue(values[fill.fieldId], fill.value))
      .map((fill) => [fill.fieldId, tooltipsBySource[fill.source ?? ""] ?? fallbackAutoFillTooltip])
  );

export const getAutoFillsOverriddenByEdit = (
  autoFilled: PreviewInvoiceCardAutoFill[],
  relatedObjectKey: string | undefined,
  oldValues: FieldIdToValueMap,
  newValues: FieldIdToValueMap
): PreviewInvoiceCardAutoFill[] =>
  autoFilled.filter(
    (fill) =>
      fill.relatedObjectKey === relatedObjectKey &&
      isSameValue(oldValues[fill.fieldId], fill.value) &&
      !isSameValue(newValues[fill.fieldId], fill.value)
  );

export const getFieldsAutoFillTooltips = (
  config: InvoiceCardPreviewConfig,
  preview: PreviewInvoiceCardResponse,
  fieldValues: FieldIdToValueMap
): Record<string, string> =>
  getTooltipsOfUnchangedFills(
    preview.autoFilled.filter((fill) => fill.relatedObjectKey === undefined),
    fieldValues,
    config.autoFillTooltipsBySource
  );

export const getActionCheckMessages = (
  checks: PreviewInvoiceCardCheck[]
): { blockers: string[]; warnings: string[] } => ({
  blockers: distinct(checks.filter((check) => check.isBlocker).map((check) => check.message)),
  warnings: distinct(
    checks.filter((check) => !check.isBlocker && check.relatedObjectKey === undefined).map((check) => check.message)
  ),
});

export const getCellDecorations = (
  config: InvoiceCardPreviewConfig,
  preview: PreviewInvoiceCardResponse,
  state: RelatedObjectValuesState
): RelatedObjectCellDecorations => {
  const getCellNotes = (isBlocker: boolean) =>
    preview.checks
      .filter((check) => check.relatedObjectKey !== undefined && check.isBlocker === isBlocker)
      .reduce<NotesByCell>((cells, check) => {
        const rowId = check.relatedObjectKey ?? "";
        const columnId = check.additionalFieldId ? `${check.fieldId}.${check.additionalFieldId}` : check.fieldId;
        cells[rowId] = { ...cells[rowId], [columnId]: check.message };
        return cells;
      }, {});

  const rows = state.rowsByObjectType[config.relatedObjectType] ?? [];

  return {
    blockerCells: getCellNotes(true),
    warningCells: getCellNotes(false),
    autoFilledCells: Object.fromEntries(
      rows.map((row) => [
        row.id,
        getTooltipsOfUnchangedFills(
          preview.autoFilled.filter((fill) => fill.relatedObjectKey === row.id),
          row,
          config.autoFillTooltipsBySource
        ),
      ])
    ),
  };
};

export const getLinesDifference = (amount: unknown, lineAmounts: unknown[]): number | undefined => {
  if (typeof amount !== "number") {
    return undefined;
  }

  const linesTotal = sumBy(lineAmounts, (lineAmount) => (typeof lineAmount === "number" ? lineAmount : 0));
  const differenceInCents = Math.round((linesTotal - amount) * 100);
  return differenceInCents === 0 ? undefined : differenceInCents / 100;
};
